import { Context } from 'context';
import { ReportType } from 'enums/reports';
import { ConditionedStockByClientByMonth } from 'pages/Stocks/ConditionedByClient/types';
import {
  AdministrationSortingCenterUserRoleForPost,
  AdministrationUserForPost,
  ChangePasswordPost,
  CharacterizationPost,
  ClientContractsPost,
  ClientPost,
  ClosureStatusEnum,
  EstimatedReceptionPost,
  EstimatedReceptionPut,
  EstimatedReceptionSinglePut,
  Event,
  ExpeditionBuyerConfirmationPost,
  ExpeditionComputeRepartitionPost,
  ExpeditionPost,
  ExpeditionRepartitionAccepteeRepreneurBatch,
  InventoriedInboundStockPost,
  InventoriedOutboundStockBatch,
  MaximumInboundStocks,
  ProductionPost,
  ProvisionalOutBoundStockQueryGroupByEnum,
  QueryParams,
  ReceptionPost,
  SingleProductionPost,
  SortingCenterContractPost,
  SortingCenterContractPut,
  SortingCenterContractsEditFormPut,
  SortingCenterUserRole,
  User,
  UserExistsByEmail,
  UserRole,
} from 'types';
import { addDaysToDate, formatDateAsQueryParam, getISOStringWithTimeZone, isValidDate } from 'utils/date';
import { ClosureInventoryStateEnum } from 'pages/Closures/types';
import client from './client';
import { notify } from '../components/Notifier';
import { transformId, transformObjectId } from './apiPlatformTransformer';

export async function closeClosure(closureId: number) {
  await client.put(`/closures/${closureId}`, {
    status: ClosureStatusEnum.CLOSED,
  });
}

export async function postReception(data: ReceptionPost) {
  await client.post('/receptions', data);
  PubSub.publish(Event.REFRESH_RECEPTIONS);
}
export async function putReception(data: ReceptionPost, receptionId: string) {
  await client.put(`/receptions/${receptionId}`, data);
  PubSub.publish(Event.REFRESH_RECEPTIONS);
}
export async function deleteReception(receptionId: string) {
  await client.delete(`/receptions/${receptionId}`);
  PubSub.publish(Event.REFRESH_RECEPTIONS);
}

export async function postSortingCenterContractAndReception(
  sortingCenterContractData: SortingCenterContractPost,
  estimatedReceptionData: EstimatedReceptionPost,
) {
  await Promise.all([
    client.post(`/sorting_center_contracts`, sortingCenterContractData),
    client.post(`/estimated_receptions`, estimatedReceptionData),
  ]);
  PubSub.publish(Event.REFRESH_ESTIMATED_RECEPTIONS);
  PubSub.publish(Event.REFRESH_SORTING_CENTER_CONTRACTS);
}
export async function putSortingCenterContractAndEstimatedReception(
  sortingCenterContractData: SortingCenterContractPut,
  estimatedReceptionData?: EstimatedReceptionSinglePut,
) {
  const sortingCenterContractId = transformId(sortingCenterContractData['@id']);
  const promises = [];
  promises.push(client.put(`/sorting_center_contracts/${sortingCenterContractId}`, sortingCenterContractData));
  if (estimatedReceptionData?.id) {
    const estimateReceptionId = transformId(estimatedReceptionData.id);
    promises.push(client.put(`/estimated_receptions/${estimateReceptionId}`, estimatedReceptionData));
  }
  await Promise.all(promises);
  PubSub.publish(Event.REFRESH_ESTIMATED_RECEPTIONS);
  if (estimatedReceptionData?.id) {
    PubSub.publish(Event.REFRESH_SORTING_CENTER_CONTRACTS);
  }
}

export async function postAdministrationUserSortingCenterUserRole(
  userData: AdministrationUserForPost,
  roles: AdministrationSortingCenterUserRoleForPost[],
) {
  const newUser = await client.post('/users', userData);
  const promises: any = [];
  roles.map((role: AdministrationSortingCenterUserRoleForPost) => {
    promises.push(client.post('/sorting_center_user_roles', { ...role, ...{ user: newUser['@id'] } }));
    return null;
  });
  await Promise.all(promises);
}

export async function putAdministrationUserSortingCenterUserRole(
  userId: string,
  roles: AdministrationSortingCenterUserRoleForPost[],
) {
  const promises: any = [];
  roles.map((role: AdministrationSortingCenterUserRoleForPost) => {
    promises.push(client.post('/sorting_center_user_roles', { ...role, ...{ user: `/api/users/${userId}` } }));
    return null;
  });
  await Promise.all(promises);
}

export async function putAdministrationSortingCenterUserRoleEdit(
  sortingCenterUserRole: SortingCenterUserRole | null | undefined,
  user: User | undefined,
  role: UserRole | undefined,
) {
  const sortingCenterUserRoleId = transformObjectId(sortingCenterUserRole);
  const data = {
    role,
    sortingCenter: sortingCenterUserRole?.sortingCenter['@id'],
    user: `/api/users/${transformObjectId(user)}`,
  };
  await client.put(`/sorting_center_user_roles/${sortingCenterUserRoleId}`, data);
  PubSub.publish(Event.REFRESH_ADMIN_USER);
}

export async function putEstimatedReceptions(data: EstimatedReceptionPut) {
  await client.put(`/estimated_reception_batches/0`, data);
}

export async function postClientContracts(data: ClientContractsPost) {
  await client.post(`/client_contracts_batch`, data);
}

export async function postExpedition(data: ExpeditionPost) {
  await client.post('/expeditions', data);
}
export async function deleteExpedition(expeditionId: string) {
  await client.delete(`/expeditions/${expeditionId}`);
  PubSub.publish(Event.REFRESH_EXPEDITIONS);
}
export async function putExpedition(data: ExpeditionPost, expeditionId: string) {
  await client.put(`/expeditions/${expeditionId}`, data);
  PubSub.publish(Event.REFRESH_EXPEDITIONS);
}

export async function postExpeditionBuyerConfirmation(data: ExpeditionBuyerConfirmationPost, withRefresh = true) {
  await client.post('/expedition_buyer_confirmations', data);

  if (withRefresh) {
    PubSub.publish(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION);
  }
}
export async function putExpeditionBuyerConfirmation(
  data: ExpeditionBuyerConfirmationPost,
  expeditionBuyerConfirmationId: string,
  withRefresh = true,
) {
  await client.put(`/expedition_buyer_confirmations/${expeditionBuyerConfirmationId}`, data);

  if (withRefresh) {
    PubSub.publish(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION);
  }
}

export async function postCharacterization(data: CharacterizationPost) {
  await client.post('/characterizations', data);
}

export async function postClient(data: ClientPost) {
  return await client.post('/clients', data);
}

export async function postExpeditionComputeRepartition(data: ExpeditionComputeRepartitionPost) {
  return await client.post('/expedition_compute_repartition', data);
}

export async function putReopenMultiClosure(sortingCenterId: string, closureId: string) {
  return await client.put(`/sorting_centers/${sortingCenterId}/closures/${closureId}/reopen-multi`, {});
}

export async function postChangePassword(data: ChangePasswordPost) {
  await client.post('/change_password', data);
}

export async function putCharacterization(data: CharacterizationPost, characterizationId: string) {
  await client.put(`/characterizations/${characterizationId}`, data);
  PubSub.publish(Event.REFRESH_CHARACTERIZATIONS);
}

export async function deleteCharacterization(characterizationId: string) {
  await client.delete(`/characterizations/${characterizationId}`);
  PubSub.publish(Event.REFRESH_CHARACTERIZATIONS);
}

export async function postProduction(data: ProductionPost) {
  await client.post('/production_batches', data);
}
export async function putProduction(data: SingleProductionPost, productionId: string) {
  await client.put(`/productions/${productionId}`, data);
  PubSub.publish(Event.REFRESH_PRODUCTIONS);
}

export async function deleteProduction(productionId: string) {
  await client.delete(`/productions/${productionId}`);
  PubSub.publish(Event.REFRESH_PRODUCTIONS);
}

export async function postInventoriedOutboundStocks(data: InventoriedOutboundStockBatch) {
  return await client.post('/inventoried_outbound_stock_batches', data);
}

export async function postExpeditionRepartitionAccepteeRepreneurBatch(
  data: ExpeditionRepartitionAccepteeRepreneurBatch,
) {
  await client.post('/expedition_repartition_acceptee_repreneur_batches', data);
}

export async function postInventoriedInboundStocks(data: InventoriedInboundStockPost[], sortingCenterId: string) {
  await client.post('/inventoried_inbound_stock_batches', {
    inventoriedInboundStocks: data,
    sortingCenter: sortingCenterId,
  });
}

export async function putSortingCenterContracts(data: SortingCenterContractsEditFormPut[], sortingCenterId: string) {
  await client.put('/sorting_center_contract_batches/0', {
    sortingCenterContracts: data,
    sortingCenter: sortingCenterId,
  });
}

export function exportShippingSlip(id: string, t: (x: string) => string): void {
  downloadFile(`/expeditions/${id}/shipping_slip`)
    .then(() => {
      notify({
        message: t('expeditions.successfullyExported.shippingSlip'),
        severity: 'success',
      });
    })
    .catch(() => {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    });
}

export function exportConfirmationShippingSlip(id: string, t: (x: string) => string): void {
  downloadFile(`/expeditions/${id}/confirmation_shipping_slip`)
    .then(() => {
      notify({
        message: t('expeditions.successfullyExported.confirmationShippingSlip'),
        severity: 'success',
      });
    })
    .catch(() => {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    });
}

export async function downloadClientMonthlyResultReport(
  sortingCenterId: string,
  year: string,
  month: string,
  clientId: number | string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.CLIENT
    }&clientId=${clientId}&preview=${Number(isPreview)}`,
  );
}

export async function downloadCharacterizationAverageReport(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.CHARACTERIZATION_AVERAGE
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadCumulatedPassageChaine(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.CUMULATED_PASSAGE_CHAINE
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadCharacterizationDetailReport(id: number) {
  await downloadFile(`/characterization/${id}/detail_report`);
}

export async function downloadClientExpeditionReport(sortingCenterId: string, startDate: Date, endDate: Date) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  let params = new URLSearchParams();
  params.append('expeditionDate[after]', formatDateAsQueryParam(startDate));
  params.append('expeditionDate[strictly_before]', formatDateAsQueryParam(addDaysToDate(endDate, 1)));
  params.append('withDay', 'true');
  params.append('order[expeditionDate]', 'asc');
  params.append('sortingCenter', sortingCenterNumericId);

  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/expeditions/
report_detailed?${params.toString()}`,
  );
}

export async function downloadClientExpeditionConfirmationReport(
  sortingCenterId: string,
  startDate: Date,
  endDate: Date,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  let params = new URLSearchParams();
  params.append('exists[expeditionBuyerConfirmation]', 'true');
  params.append('expeditionBuyerConfirmation.confirmationDate[after]', formatDateAsQueryParam(startDate));
  params.append(
    'expeditionBuyerConfirmation.confirmationDate[strictly_before]',
    formatDateAsQueryParam(addDaysToDate(endDate, 1)),
  );
  params.append('withDay', 'true');
  params.append('order[expeditionBuyerConfirmation.confirmationDate]', 'asc');
  params.append('sortingCenter', sortingCenterNumericId);

  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/expeditions/
report_confirmed?${params.toString()}`,
  );
}

export async function downloadClientExpeditionValidatedReport(sortingCenterId: string, startDate: Date, endDate: Date) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  let params = new URLSearchParams();
  params.append('expeditionDate[after]', formatDateAsQueryParam(startDate));
  params.append('expeditionDate[strictly_before]', formatDateAsQueryParam(addDaysToDate(endDate, 1)));
  params.append('withDay', 'true');
  params.append('order[expeditionDate]', 'asc');
  params.append('sortingCenter', sortingCenterNumericId);

  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/expeditions/
report_validated?${params.toString()}`,
  );
}

export async function downloadAllReports(sortingCenterId: string, year: string, month: string, isPreview: boolean) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.ALL
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadConditionedStock(sortingCenterId: string, startDate: Date, endDate: Date) {
  let params = new URLSearchParams();
  const sortingCenterNumericId = transformId(sortingCenterId);

  params.append('startDate', getISOStringWithTimeZone(startDate));
  params.append('endDate', getISOStringWithTimeZone(endDate));

  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/inventoried_inbound_stocks/report_conditioned_stocks?${params.toString()}`,
  );
}

export async function downloadGlobalReportTheoreticalAndRealised(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.GLOBAL_THEORETICAL_AND_REALISED
    }&preview=${Number(isPreview)}`,
  );
}
export async function downloadGlobalReportMaterialResults(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = transformId(sortingCenterId);
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.GLOBAL_REPORT_RESULT_AND_MATERIAL
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadGlobalReportDistributionKeys(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = transformId(sortingCenterId);
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.DISTRIBUTION_KEYS
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadGlobalReportProductionExpedition(
  sortingCenterId: string,
  startDate: Date,
  endDate: Date,
  isPreview: boolean,
) {
  let params = new URLSearchParams();
  const sortingCenterNumericId = transformId(sortingCenterId);

  params.append('startDate', getISOStringWithTimeZone(startDate));
  params.append('endDate', getISOStringWithTimeZone(endDate));
  params.append('preview', isPreview ? '1' : '0');

  await downloadFile(`/sorting_centers/${sortingCenterNumericId}/report_production_expedition/?${params.toString()}`);
}
export async function downloadGlobalReportCumulatedMaterialResult(
  sortingCenterId: string,
  startDate: Date,
  endDate: Date,
) {
  let params = new URLSearchParams();
  const sortingCenterNumericId = transformId(sortingCenterId);

  params.append('startDate', getISOStringWithTimeZone(startDate));
  params.append('endDate', getISOStringWithTimeZone(endDate));

  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/materials/report_cumulated_result?${params.toString()}`,
  );
}
export async function downloadCumulatedMonhlyReport(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.CUMULATED_MONTHLY_REPORT_EXPORTER
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadStocksAmontsFinauxReport(
  sortingCenterId: string,
  year: string,
  month: string,
  isPreview: boolean,
) {
  const sortingCenterNumericId = sortingCenterId.split('/')[3];
  await downloadFile(
    `/sorting_centers/${sortingCenterNumericId}/closures/${year}-${month}/report?type=${
      ReportType.STOCKS_AMONTS_FINAUX
    }&preview=${Number(isPreview)}`,
  );
}

export async function downloadFile(fileURL: string, defaultName?: string) {
  let anchor = document.createElement('a');
  document.body.appendChild(anchor);
  let file = `${process.env.REACT_APP_API_BASE_URL}` + fileURL;

  await client.checkToken();
  await client.agent
    .get(file)
    .set('Authorization', `Bearer ${client.getToken()}`)
    .responseType('blob')
    .then(res => {
      const objectUrl = window.URL.createObjectURL(res.body);
      anchor.href = objectUrl;
      // @ts-ignore
      anchor.download =
        res.headers['content-disposition'].match(/filename\*?=['"]?(?:utf-\d['"]*)?([^;\r\n"']*)['"]?;?/)?.[1] ||
        defaultName;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
}

export async function acceptTos(context: Context) {
  await client.post(`/accept-tos`, {});
  await client.refreshToken();
  const user = await client.get('/auth/me');
  context.setUser(user);
}

export async function generateFixtures(context: Context) {
  await client.post('/reset-fixtures', {});
}

export async function createInitClosures() {
  await client.post('/create-init-closures', {});
}

export async function downloadProvisionalOutboundStocksForClientConditionedStock(
  sortingCenterId: string,
  startDate: Date,
  endDate: Date,
  clientId?: string,
  materialId?: string,
) {
  let params = new URLSearchParams();

  params.append('startDate', getISOStringWithTimeZone(startDate));
  params.append('endDate', getISOStringWithTimeZone(endDate));

  if (clientId) params.append('clientId', clientId);
  if (materialId) params.append('materialId', materialId);

  return await downloadFile(`/sorting_centers/${sortingCenterId}/outbound_stocks/report?${params.toString()}`);
}

export async function getProvisionalOutboundStocksForClientConditionedStock(
  sortingCenterId: string,
  startDate: Date,
  endDate: Date,
  clientIds: string[] = [],
  materialIds: string[] = [],
): Promise<ConditionedStockByClientByMonth[]> {
  let params = new URLSearchParams();

  if (startDate && isValidDate(startDate) && endDate && isValidDate(endDate)) {
    params.append('startDate', startDate.toISOString());
    params.append('endDate', endDate.toISOString());
  }

  for (let clientId of clientIds) {
    params.append('clientIds[]', clientId);
  }

  for (let materialId of materialIds) {
    params.append('materialIds[]', materialId);
  }

  params.append('groupBy', ProvisionalOutBoundStockQueryGroupByEnum.CLIENT_AND_MATERIAL);

  const result = await client.get(
    `/sorting_centers/${sortingCenterId}/provisional_outbound_stocks?${params.toString()}`,
    { fullbody: true },
  );

  const provisionalOutboundStockByMonths: ConditionedStockByClientByMonth[] = result?.['hydra:member']?.[0];
  const provisionalOutboundStocksForClientConditionedStock = [];

  for (let i = 0; i < provisionalOutboundStockByMonths.length; i++) {
    const provisionalOutboundStockByMonth = provisionalOutboundStockByMonths[i];
    let clientName = '';

    const isFirst = i === 0;
    const isDifferentClient =
      !isFirst && provisionalOutboundStockByMonths[i - 1].client !== provisionalOutboundStockByMonth.client;
    const isDifferentMonth =
      !isFirst &&
      (provisionalOutboundStockByMonths[i - 1].year !== provisionalOutboundStockByMonth.year ||
        (provisionalOutboundStockByMonths[i - 1].year === provisionalOutboundStockByMonth.year &&
          provisionalOutboundStockByMonths[i - 1].month !== provisionalOutboundStockByMonth.month));

    const shouldWriteClientName = isFirst || isDifferentClient || isDifferentMonth;

    if (shouldWriteClientName) {
      clientName = provisionalOutboundStockByMonth.client;
    }

    provisionalOutboundStocksForClientConditionedStock.push({
      client: clientName,
      material: provisionalOutboundStockByMonth.material,
      weight: provisionalOutboundStockByMonth.weight / 1000,
      year: provisionalOutboundStockByMonth.year,
      month: provisionalOutboundStockByMonth.month,
    });
  }

  return provisionalOutboundStocksForClientConditionedStock;
}

export function constructResultFromApi(result: any) {
  return {
    results: result['hydra:member'],
    total: result['hydra:totalItems'],
  };
}

export function generatePathWithFilters(apiPath: string, params: QueryParams) {
  const stringParams = new URLSearchParams(params as Record<string, string>);
  let path = apiPath;

  if (!!stringParams.toString()) {
    path = path + '?' + stringParams;
  }

  return path;
}

export async function findUserByEmail(email: string): Promise<UserExistsByEmail | undefined> {
  return await client.get(`/admin/user?email=${email}`);
}

export async function deleteSortingCenterUserRoles(id: string) {
  await client.delete(`/sorting_center_user_roles/${id}`);
  PubSub.publish(Event.REFRESH_ADMIN_USER);
}

export async function deleteAllSortingCenterUserRolesByAdminSortingCenter(id: string) {
  await client.delete(`/admin/user/sorting_center_user_role/${id}`);
}

export async function putClosureInventoryState(closureId: string, inventoryState: ClosureInventoryStateEnum) {
  return await client.put(`/closures/${closureId}`, {
    inventoriedOutboundStockState: inventoryState,
  });
}

export async function putUserEmail(userId: string, email: string) {
  await client.put(`/users/${userId}`, {
    email: email,
  });
  PubSub.publish(Event.REFRESH_ADMIN_USER);
}

export async function postSortingCenterUserRoleMerge(userFrom: User, userTo: UserExistsByEmail | null) {
  return await client.post(`/admin/user/sorting_center_user_role_merge`, {
    userFrom: `/api/users/${transformObjectId(userFrom)}`,
    userTo: userTo && userTo.user ? `/api/users/${userTo.user['id']}` : '',
  });
}

export async function getMaximumInboundStocks(
  sortingCenterId: number,
  year: number,
): Promise<MaximumInboundStocks | undefined> {
  return await client.get(`/sorting_centers/${sortingCenterId}/inbound_stocks/${year}/maximum_inbound_stocks`);
}

export async function getImportReceptionsReport(sortingCenterId: string, data: string) {
  return await downloadFile(`/sorting_centers/${sortingCenterId}/receptions/error_report?data=${data}`);
}
